import { useQuery } from "@tanstack/react-query";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
  animate as animateValue,
} from "framer-motion";
import { Cpu, Loader2, Zap } from "lucide-react";
import * as React from "react";

import {
  fetchCostPreview,
  type CostPreviewResponse,
  type ResourceOverride,
} from "@/api/submission";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * SIGNATURE COMPONENT — sticky right-rail, live cost preview.
 *
 * Reacts to every parameter edit. The total ΔG-style number rolls
 * smoothly on change; per-tile resource bars use ``layout`` so they
 * animate into their new widths; a transient "+$0.42" / "-$0.10" diff
 * chip flies up and fades when the total changes. This is the moment
 * the user feels the platform is *thinking with them*.
 *
 * Implementation notes:
 * - Debounce parameter edits (250ms) before firing the cost-preview
 *   request — otherwise typing burns API calls.
 * - Re-fetch on key change so a Tile swap drops the cached estimate.
 * - Respects ``prefers-reduced-motion`` — the number roll degrades to
 *   instant; the layout animation is preserved since it carries
 *   information about the resource breakdown.
 */
export interface CostPreviewPanelProps {
  tileName: string | null;
  parameters: Record<string, unknown>;
  /** Submit-time resource-class override (flowstack#620). Threaded into
   * the estimate so a GPU opt-in reprices as GPU-hours live, matching
   * what the run will actually cost. */
  resources?: ResourceOverride;
  className?: string;
}

export function CostPreviewPanel({
  tileName,
  parameters,
  resources,
  className,
}: CostPreviewPanelProps): JSX.Element {
  const debounced = useDebounced(parameters, 250);
  // The resource selection is a small discrete control (no keystroke
  // storms), so it keys the query directly without the parameter debounce.
  const resourcesKey = JSON.stringify(resources ?? null);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ["cost-preview", tileName, JSON.stringify(debounced), resourcesKey],
    queryFn: async (): Promise<CostPreviewResponse> => {
      if (!tileName) throw new Error("no tile selected");
      return fetchCostPreview({ tile_name: tileName, parameters: debounced, resources });
    },
    enabled: Boolean(tileName),
    staleTime: 30_000,
  });

  return (
    <Card
      as="aside"
      elevation="raised"
      className={cn("sticky top-20 flex flex-col gap-4 p-card", className)}
      aria-label="Cost preview"
    >
      <header className="flex items-center justify-between">
        <h3 className="font-display text-base font-semibold tracking-tight">Cost preview</h3>
        {isFetching && (
          <Loader2
            className="h-3.5 w-3.5 text-muted-foreground motion-safe:animate-arc-spin"
            aria-label="Updating"
          />
        )}
      </header>

      {!tileName ? (
        <p className="text-sm text-muted-foreground">
          Select a Tile to see an estimate. The number updates live as you edit parameters.
        </p>
      ) : isError ? (
        <p className="text-sm text-state-failed-validation-fg">
          Estimate unavailable: {error instanceof Error ? error.message : "unknown error"}
        </p>
      ) : !data ? (
        <SkeletonRows />
      ) : (
        <CostBody response={data} />
      )}

      {/* Estimate badge is driven by the server's pricing_basis
          (U0.3, C0.5): rendered only for "placeholder-estimate";
          absent field (older server) ⇒ no badge, so the GUI never
          claims placeholder pricing it can't verify. */}
      {data?.pricing_basis === "placeholder-estimate" && (
        <footer className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          <span className="rounded-sm bg-surface-2 px-1 py-0.5">estimate</span>
          <span>
            based on manifest-declared resource bounds &amp; the pilot&apos;s placeholder pricing.
          </span>
        </footer>
      )}
    </Card>
  );
}

function CostBody({ response }: { response: CostPreviewResponse }): JSX.Element {
  const cpu = response.estimated_cpu_hours;
  const gpu = response.estimated_gpu_hours;
  const total = cpu + gpu;

  return (
    <div className="flex flex-col gap-4">
      <RollingDollar value={response.estimated_total_usd} />

      <DiffChip value={response.estimated_total_usd} />

      <motion.div layout className="flex flex-col gap-2">
        {cpu > 0 && (
          <ResourceRow
            label="CPU"
            icon={<Cpu className="h-3.5 w-3.5" aria-hidden />}
            value={`${cpu.toFixed(2)} h`}
            ratio={total > 0 ? cpu / total : 0}
            color="bg-state-staging"
          />
        )}
        {gpu > 0 && (
          <ResourceRow
            label="GPU"
            icon={<Zap className="h-3.5 w-3.5" aria-hidden />}
            value={`${gpu.toFixed(2)} h`}
            ratio={total > 0 ? gpu / total : 0}
            color="bg-state-running"
          />
        )}
        {cpu === 0 && gpu === 0 && (
          <p className="text-xs text-muted-foreground">
            No measurable compute — this Tile is metadata-only.
          </p>
        )}
      </motion.div>
    </div>
  );
}

function SkeletonRows(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <div className="h-9 w-32 rounded-md bg-surface-2 motion-safe:animate-pulse" />
      <div className="h-3 rounded-full bg-surface-2 motion-safe:animate-pulse" />
      <div className="h-3 rounded-full bg-surface-2 motion-safe:animate-pulse" />
    </div>
  );
}

interface ResourceRowProps {
  label: string;
  icon: React.ReactNode;
  value: string;
  ratio: number;
  color: string;
}

function ResourceRow({ label, icon, value, ratio, color }: ResourceRowProps): JSX.Element {
  return (
    <motion.div layout className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          {icon}
          {label}
        </span>
        <span className="font-mono tabular-nums text-foreground">{value}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
        <motion.div
          layout
          initial={false}
          animate={{ width: `${Math.max(2, ratio * 100)}%` }}
          transition={{ type: "spring", stiffness: 220, damping: 28 }}
          className={cn("h-full rounded-full", color)}
        />
      </div>
    </motion.div>
  );
}

/** Rolling $-number — animates from previous to new on every change.
 * The roll length is bounded so big jumps don't take forever. */
function RollingDollar({ value }: { value: number }): JSX.Element {
  const reduceMotion = useReducedMotion();
  const motionValue = useMotionValue(value);
  const display = useTransform(motionValue, (v) => `$${v.toFixed(2)}`);

  React.useEffect(() => {
    if (reduceMotion) {
      motionValue.set(value);
      return;
    }
    const controls = animateValue(motionValue, value, {
      duration: Math.min(0.6, Math.max(0.2, Math.abs(value - motionValue.get()) / 5)),
      ease: "easeOut",
    });
    return controls.stop;
  }, [value, motionValue, reduceMotion]);

  return (
    <div className="flex items-baseline gap-1">
      <motion.span
        className="font-display text-4xl font-semibold tabular-nums tracking-tight text-foreground"
        aria-live="polite"
      >
        {display}
      </motion.span>
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        USD
      </span>
    </div>
  );
}

/** Chip-management hook — exported so the regression suite can pin
 * down the leak behaviour by inspecting ``chips`` directly, without
 * fighting AnimatePresence's exit animation under fake timers.
 *
 * Per Rupert PR-260: the previous effect returned a cleanup that
 * cleared its own removal timer, so a rapid second value change
 * cancelled the *prior* chip's removal — chips piled up in the
 * state array without ever exiting. Track all pending timers in a
 * ref and clear them only on unmount; the closure-captured id
 * makes per-chip removal idempotent.
 *
 * Hook returns the live chip list; the visual ``DiffChip`` below
 * is the thin rendering layer that maps them to motion.spans. */
interface Chip {
  id: number;
  delta: number;
}

export function useDiffChips(value: number, lifetimeMs: number = 900): Chip[] {
  const reduceMotion = useReducedMotion();
  const prev = React.useRef(value);
  const [chips, setChips] = React.useState<Chip[]>([]);
  const counter = React.useRef(0);
  const timers = React.useRef<Set<number>>(new Set());

  React.useEffect(() => {
    if (prev.current === value) return;
    const delta = value - prev.current;
    prev.current = value;
    if (Math.abs(delta) < 0.005 || reduceMotion) return;
    const id = ++counter.current;
    setChips((c) => [...c, { id, delta }]);
    const t = window.setTimeout(() => {
      setChips((c) => c.filter((ch) => ch.id !== id));
      timers.current.delete(t);
    }, lifetimeMs);
    timers.current.add(t);
  }, [value, reduceMotion, lifetimeMs]);

  // Clean up any in-flight removal timers on unmount only — not on
  // every value change.
  React.useEffect(() => {
    const set = timers.current;
    return () => {
      set.forEach(window.clearTimeout);
      set.clear();
    };
  }, []);

  return chips;
}

/** Transient diff chip — "+$0.42" / "-$0.10" floats up and fades on
 * each total change. Helps the user feel which edits move the number
 * (rather than scrubbing through the whole history).
 *
 * Pure rendering wrapper over :func:`useDiffChips`. */
export function DiffChip({ value }: { value: number }): JSX.Element {
  const chips = useDiffChips(value);
  return (
    <div className="relative h-0">
      <AnimatePresence>
        {chips.map((c) => (
          <motion.span
            key={c.id}
            initial={{ y: 0, opacity: 0 }}
            animate={{ y: -16, opacity: 1 }}
            exit={{ y: -28, opacity: 0 }}
            transition={{ duration: 0.85, ease: "easeOut" }}
            className={cn(
              "pointer-events-none absolute left-0 rounded-full border px-2 py-0.5 font-mono text-[11px] tabular-nums",
              c.delta > 0
                ? "border-state-failed-validation-border bg-state-failed-validation-bg text-state-failed-validation-fg"
                : "border-state-completed-border bg-state-completed-bg text-state-completed-fg"
            )}
          >
            {c.delta > 0 ? "+" : ""}
            {c.delta.toFixed(2)}
          </motion.span>
        ))}
      </AnimatePresence>
    </div>
  );
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = window.setTimeout(() => setV(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return v;
}
